import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  useListContext,
  TopToolbar,
  ShowButton,
  useRefresh,
  Loading,
  ReferenceInput,
  AutocompleteInput,
  FilterButton,
  CreateButton,
  SelectArrayInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  useTranslate,
  useNotify,
  usePermissions,
  RecordContextProvider
} from 'react-admin';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { apiClient } from '../../utils/apiClient';
import { API_CONFIG } from '../../config/api';
import { useReportExport, ColumnConfig } from '../../hooks/useReportExport';
import {
  Chip,
  Box,
  Typography,
  Button,
  CircularProgress,
  Backdrop,
  useMediaQuery,
  useTheme,
  Card,
  CardActionArea,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Bolt,
  CreditCard,
  AccountBalanceWallet,
  CreditScore,
  QrCode2,
  Badge
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DateTimeFilterInput } from '../common/DateTimeFilterInput';

// Filter components as a hook function
const useSessionFilters = () => {
  const translate = useTranslate();

  return [
    <TextInput key="search" label={translate('resources.charging-sessions.filters.search')} source="search" alwaysOn />,
    <SelectArrayInput
      key="initiated_by"
      label={translate('resources.charging-sessions.filters.type')}
      source="initiated_by"
      choices={[
        { id: 'app', name: translate('resources.charging-sessions.types.app') },
        { id: 'rfid', name: translate('resources.charging-sessions.types.rfid') },
        { id: 'admin', name: translate('resources.charging-sessions.types.admin') },
        { id: 'ocpp', name: translate('resources.charging-sessions.types.ocpp') }
      ]}
    />,
    <ReferenceArrayInput
      key="site_id"
      label={translate('resources.charging-sessions.filters.site')}
      source="site_id"
      reference="sites"
    >
      <AutocompleteArrayInput
        optionText="name"
        label={translate('resources.charging-sessions.filters.site')}
        filterToQuery={(searchText: string) => ({ search: searchText })}
      />
    </ReferenceArrayInput>,
    <ReferenceArrayInput
      key="charger_id"
      label={translate('resources.charging-sessions.filters.charger')}
      source="charger_id"
      reference="chargers"
    >
      <AutocompleteArrayInput
        optionText="name"
        label={translate('resources.charging-sessions.filters.charger')}
        filterToQuery={(searchText: string) => ({ search: searchText })}
      />
    </ReferenceArrayInput>,
    <SelectArrayInput
      key="payment_status"
      label={translate('resources.charging-sessions.filters.paymentStatus')}
      source="payment_status"
      choices={[
        { id: 'completed', name: translate('resources.charging-sessions.paymentStatuses.completed') },
        { id: 'failed', name: translate('resources.charging-sessions.paymentStatuses.failed') },
        { id: 'refunded', name: translate('resources.charging-sessions.paymentStatuses.refunded') },
        { id: 'partial_refund', name: translate('resources.charging-sessions.paymentStatuses.partial_refund') }
      ]}
    />,
    <DateTimeFilterInput key="date_from" label={translate('resources.charging-sessions.filters.fromDate')} source="date_from" defaultTime="00:00" />,
    <DateTimeFilterInput key="date_to" label={translate('resources.charging-sessions.filters.toDate')} source="date_to" defaultTime="23:59" />
  ];
};

// Payment status chip component
const PaymentStatusChip = ({ status }: { status: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'refunded': return 'warning';
      case 'partial_refund': return 'info';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={translate(`resources.charging-sessions.paymentStatuses.${status}`, { _: status })}
      color={getColor() as any}
      size="small"
    />
  );
};

// Payment method icon
const PaymentMethodIcon = ({ method }: { method: string }) => {
  switch (method) {
    case 'wallet': return <AccountBalanceWallet sx={{ fontSize: 18 }} />;
    case 'credit_card': return <CreditCard sx={{ fontSize: 18 }} />;
    case 'rfid': return <Badge sx={{ fontSize: 18 }} />;
    case 'qr_code': return <QrCode2 sx={{ fontSize: 18 }} />;
    default: return null;
  }
};

// Initiated by chip
const InitiatedByChip = ({ initiatedBy }: { initiatedBy: string }) => {
  const translate = useTranslate();

  const getColor = () => {
    switch (initiatedBy) {
      case 'app': return 'primary';
      case 'rfid': return 'secondary';
      case 'admin': return 'warning';
      case 'backend_standalone': return 'warning';
      case 'backend_on_behalf': return 'warning';
      case 'ocpp': return 'info';
      case 'vendor_api': return 'info';
      default: return 'default';
    }
  };

  return (
    <Chip
      label={translate(`resources.charging-sessions.types.${initiatedBy}`, { _: initiatedBy?.toUpperCase() || 'UNKNOWN' })}
      color={getColor() as any}
      size="small"
      variant="outlined"
    />
  );
};

// Duration display
const DurationDisplay = ({ seconds }: { seconds: number }) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return <Typography variant="body2">{hours}h {minutes}m</Typography>;
  }
  return <Typography variant="body2">{minutes} min</Typography>;
};

type SessionExportRow = {
  app_session_id: string | null;
  user_name: string | null;
  user_email: string | null;
  user_phone: string | null;
  car_plate: string | null;
  charger_id: string | null;
  charger_name: string | null;
  site_name: string | null;
  session_start_time: string | null;
  session_end_time: string | null;
  charging_duration_seconds: number | null;
  idle_duration_seconds: number | null;
  energy_delivered_kwh: number | null;
  charging_service_fee: number | null;
  idle_fee: number | null;
  final_cost: number | null;
  currency: string | null;
  payment_status: string | null;
  initiated_by: string | null;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds == null) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const formatDateTime = (value: any): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const EXPORT_LIMIT = 50000;

const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

type PresetKey = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth';

const computePreset = (key: PresetKey): { from: Date; to: Date } => {
  const now = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  switch (key) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'yesterday': {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'last7days': {
      const from = new Date(now);
      from.setDate(from.getDate() - 6); // last 7 days inclusive of today
      return { from: startOfDay(from), to: endOfDay(now) };
    }
    case 'last30days': {
      const from = new Date(now);
      from.setDate(from.getDate() - 29);
      return { from: startOfDay(from), to: endOfDay(now) };
    }
    case 'thisMonth': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      return { from, to: endOfDay(now) };
    }
    case 'lastMonth': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
      const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { from, to: lastDayLastMonth };
    }
  }
};

const ChargingSessionsExportButton = () => {
  const { sort, isLoading } = useListContext();
  const translate = useTranslate();
  const notify = useNotify();
  const { exportToExcel, exporting } = useReportExport();

  // Default the dialog to "last 30 days" so a click-and-go export is bounded.
  const defaultRange = React.useMemo(() => {
    const { from, to } = computePreset('last30days');
    return { from: toLocalInputValue(from), to: toLocalInputValue(to) };
  }, []);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dateFrom, setDateFrom] = React.useState<string>(defaultRange.from);
  const [dateTo, setDateTo] = React.useState<string>(defaultRange.to);
  const [activePreset, setActivePreset] = React.useState<PresetKey | null>('last30days');

  const openDialog = () => {
    // Always reset to the safe "last 30 days" default each time the dialog opens.
    setDateFrom(defaultRange.from);
    setDateTo(defaultRange.to);
    setActivePreset('last30days');
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const applyPreset = (key: PresetKey) => {
    const { from, to } = computePreset(key);
    setDateFrom(toLocalInputValue(from));
    setDateTo(toLocalInputValue(to));
    setActivePreset(key);
  };

  const buildExportParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('limit', String(EXPORT_LIMIT));
    if (sort?.field) params.set('sort', sort.field);
    if (sort?.order) params.set('order', sort.order.toLowerCase());

    if (dateFrom) params.set('date_from', new Date(dateFrom).toISOString());
    if (dateTo) params.set('date_to', new Date(dateTo).toISOString());
    return params;
  };

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      notify(translate('resources.charging-sessions.export.dateRequired'), { type: 'warning' });
      return;
    }
    if (new Date(dateFrom).getTime() > new Date(dateTo).getTime()) {
      notify(translate('resources.charging-sessions.export.dateOrderInvalid'), { type: 'warning' });
      return;
    }

    try {
      const params = buildExportParams();
      const response = await apiClient.get<{ data: SessionExportRow[]; total: number }>(
        `${API_CONFIG.endpoints.backend.chargingSessionsExport}?${params.toString()}`,
      );

      if (!response.data || response.data.length === 0) {
        notify(translate('resources.charging-sessions.export.empty'), { type: 'warning' });
        return;
      }

      const columns: ColumnConfig[] = [
        { key: 'app_session_id', label: translate('resources.charging-sessions.export.col.sessionId') },
        { key: 'user_name', label: translate('resources.charging-sessions.export.col.userName') },
        { key: 'user_email', label: translate('resources.charging-sessions.export.col.userEmail') },
        { key: 'user_phone', label: translate('resources.charging-sessions.export.col.userPhone') },
        { key: 'car_plate', label: translate('resources.charging-sessions.export.col.licensePlate') },
        { key: 'charger_id', label: translate('resources.charging-sessions.export.col.chargerId') },
        { key: 'charger_name', label: translate('resources.charging-sessions.export.col.chargerName') },
        { key: 'site_name', label: translate('resources.charging-sessions.export.col.siteName') },
        { key: 'session_start_time', label: translate('resources.charging-sessions.export.col.startTime'), format: formatDateTime },
        { key: 'session_end_time', label: translate('resources.charging-sessions.export.col.endTime'), format: formatDateTime },
        {
          key: 'charging_duration_seconds',
          label: translate('resources.charging-sessions.export.col.duration'),
          format: (v: any) => formatDuration(typeof v === 'number' ? v : null),
        },
        {
          key: 'idle_duration_seconds',
          label: translate('resources.charging-sessions.export.col.idleDuration'),
          format: (v: any) => formatDuration(typeof v === 'number' ? v : null),
        },
        { key: 'energy_delivered_kwh', label: translate('resources.charging-sessions.export.col.energyKwh'), format: 'number' },
        { key: 'charging_service_fee', label: translate('resources.charging-sessions.export.col.chargingServiceFee'), format: 'currency' },
        { key: 'idle_fee', label: translate('resources.charging-sessions.export.col.idleFee'), format: 'currency' },
        { key: 'final_cost', label: translate('resources.charging-sessions.export.col.finalCost'), format: 'currency' },
        { key: 'payment_status', label: translate('resources.charging-sessions.export.col.paymentStatus') },
        { key: 'initiated_by', label: translate('resources.charging-sessions.export.col.initiatedBy') },
      ];

      await exportToExcel(response.data, 'charging-history', {
        columns,
        sheetName: translate('resources.charging-sessions.export.sheetName'),
        translate: (key: string, opts?: any) => translate(key, opts),
        includeTimestamp: true,
        filters: {
          startDate: dateFrom,
          endDate: dateTo,
        },
      });

      // Warn loudly if we hit the row cap so the user knows to narrow the range.
      const truncated = response.total > response.data.length || response.data.length >= EXPORT_LIMIT;
      if (truncated) {
        notify(
          translate('resources.charging-sessions.export.truncated', {
            exported: response.data.length,
            total: response.total,
          }),
          { type: 'warning', autoHideDuration: 10000 },
        );
      } else {
        notify(
          translate('resources.charging-sessions.export.succeeded', { count: response.data.length }),
          { type: 'success' },
        );
      }
      closeDialog();
    } catch (err: any) {
      console.error('Charging history export failed:', err);
      notify(
        err?.message || translate('resources.charging-sessions.export.failed'),
        { type: 'error' },
      );
    }
  };

  return (
    <>
      <Button
        startIcon={<FileDownloadIcon />}
        onClick={openDialog}
        variant="outlined"
        size="small"
        disabled={isLoading || exporting}
      >
        {exporting
          ? translate('resources.charging-sessions.export.exporting')
          : translate('resources.charging-sessions.export.button')}
      </Button>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('resources.charging-sessions.export.dialog.title')}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            {translate('resources.charging-sessions.export.dialog.help', { limit: EXPORT_LIMIT })}
          </Alert>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {translate('resources.charging-sessions.export.dialog.presets')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {(['today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth'] as PresetKey[]).map((key) => (
              <Chip
                key={key}
                label={translate(`resources.charging-sessions.export.dialog.preset.${key}`)}
                onClick={() => applyPreset(key)}
                color={activePreset === key ? 'primary' : 'default'}
                variant={activePreset === key ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <MuiTextField
              label={translate('resources.charging-sessions.export.dialog.from')}
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setActivePreset(null);
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <MuiTextField
              label={translate('resources.charging-sessions.export.dialog.to')}
              type="datetime-local"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setActivePreset(null);
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={exporting}>
            {translate('ra.action.cancel')}
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          >
            {exporting
              ? translate('resources.charging-sessions.export.exporting')
              : translate('resources.charging-sessions.export.dialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const DesktopListActions = () => {
  const { total, isLoading } = useListContext();
  const refresh = useRefresh();
  const translate = useTranslate();
  const { permissions } = usePermissions();
  const canExport = Array.isArray(permissions) && permissions.includes('charging-sessions:export:all');

  return (
    <TopToolbar>
      <FilterButton />
      <Button
        startIcon={<RefreshIcon />}
        onClick={refresh}
        variant="outlined"
        size="small"
        disabled={isLoading}
      >
        {translate('resources.charging-sessions.actions.refresh')}
      </Button>
      {canExport && <ChargingSessionsExportButton />}
      <Chip
        label={`${total || 0} ${translate('resources.charging-sessions.list.totalSessions')}`}
        color="primary"
        size="small"
        sx={{ ml: 1 }}
      />
    </TopToolbar>
  );
};

const MobileListActions = () => {
  const { total, isLoading } = useListContext();
  const refresh = useRefresh();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0, gap: 1, alignItems: 'center' }}>
      <Chip
        label={`${total || 0}`}
        color="primary"
        size="small"
      />
      <IconButton
        onClick={refresh}
        disabled={isLoading}
        size="small"
        color="primary"
      >
        <RefreshIcon />
      </IconButton>
    </Box>
  );
};

// Custom Datagrid with loading overlay
const SessionDatagrid = (props: any) => {
  const { isLoading } = useListContext();
  const translate = useTranslate();

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Loading overlay */}
      {isLoading && (
        <Backdrop
          open={true}
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {translate('resources.charging-sessions.list.loadingSessions')}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {/* Actual Datagrid */}
      <Datagrid {...props} />
    </Box>
  );
};

const MobileSessionsList = () => {
  const { data, isLoading } = useListContext();
  const translate = useTranslate();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {translate('ra.navigation.no_results')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {data.map((record: any) => {
        const isRfidUser = record.rfid_user_id && !record.user_id;
        const userName = record.user_name || record.user_email || (isRfidUser ? translate('resources.charging-sessions.list.rfidUser') : translate('resources.charging-sessions.list.unknownUser'));
        const startTime = record.session_start_time ? new Date(record.session_start_time).toLocaleString() : '';

        return (
          <RecordContextProvider key={record.id} value={record}>
            <Card variant="outlined">
              <CardActionArea
                onClick={() => navigate(`/charging-sessions/${record.id}/show`)}
                sx={{ p: 1.5 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight="500"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {userName}
                  </Typography>
                  <Box sx={{ flexShrink: 0 }}>
                    <InitiatedByChip initiatedBy={record.initiated_by} />
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {record.site_name}{record.charger_name ? ` \u00B7 ${record.charger_name}` : ''}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {startTime}
                  </Typography>
                  {record.energy_kwh != null && record.energy_kwh > 0 && (
                    <Typography variant="caption" fontWeight="500">
                      {record.energy_kwh.toFixed(2)} kWh
                    </Typography>
                  )}
                </Box>
              </CardActionArea>
            </Card>
          </RecordContextProvider>
        );
      })}
    </Box>
  );
};

export const ChargingSessionsList = () => {
  const translate = useTranslate();
  const sessionFilters = useSessionFilters();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  return (
    <List
      filters={sessionFilters}
      actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
      perPage={25}
      sort={{ field: 'session_start_time', order: 'DESC' }}
      title={translate('resources.charging-sessions.list.title')}
      sx={{
        '& .RaList-content': {
          backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
        },
        ...(isMobile && {
          '& .RaList-main': { margin: 0 },
          '& .RaList-content': {
            backgroundColor: 'transparent !important',
            boxShadow: 'none !important',
            mt: 2,
          },
          '& .RaList-actions': {
            backgroundColor: 'transparent !important',
            p: '0 !important',
            minHeight: 'auto !important',
          },
          '& .RaListToolbar-root': {
            backgroundColor: 'transparent !important',
            flexDirection: 'row !important',
            flexWrap: 'wrap !important',
            alignItems: 'center !important',
            gap: '8px !important',
          },
          '& .RaListToolbar-root > span': { display: 'none' },
          '& .RaListToolbar-root > form, & .RaFilterForm-root': {
            flex: '1 1 100% !important',
            width: '100% !important',
          },
          '& .filter-field, & .RaFilterForm-filterFormInput': {
            width: '100% !important',
          },
          '& .filter-field .MuiFormControl-root': { width: '100% !important' },
          '& .filter-field .MuiInputBase-root': { width: '100% !important' },
          '& .MuiToolbar-root': {
            backgroundColor: 'transparent !important',
            minHeight: 'auto !important',
          },
        }),
      }}
    >
      {isMobile ? (
        <MobileSessionsList />
      ) : (
        <SessionDatagrid
          bulkActionButtons={false}
          sx={{
            '& .RaDatagrid-headerCell': {
              backgroundColor: '#F5F5F5',
              fontWeight: 'bold',
            },
            '& .RaDatagrid-row': {
              '&:hover': {
                backgroundColor: '#F0F7FF',
              },
            },
          }}
        >
          <FunctionField
            label={translate('resources.charging-sessions.list.session')}
            render={(record: any) => {
              const sessionId = record.app_session_id || '';
              const truncated = sessionId.length > 20
                ? `${sessionId.substring(0, 8)}...${sessionId.substring(sessionId.length - 8)}`
                : sessionId;
              return (
                <Typography variant="body2" fontFamily="monospace">
                  {truncated}
                </Typography>
              );
            }}
          />

          <FunctionField
            label={translate('resources.charging-sessions.list.user')}
            render={(record: any) => {
              const isRfidUser = record.rfid_user_id && !record.user_id;
              const userName = record.user_name || record.user_email || (isRfidUser ? translate('resources.charging-sessions.list.rfidUser') : translate('resources.charging-sessions.list.unknownUser'));

              return (
                <Box>
                  <Typography variant="body2" fontWeight="500">
                    {userName}
                  </Typography>
                  {record.user_phone && (
                    <Typography variant="caption" color="text.secondary">
                      {record.user_phone}
                    </Typography>
                  )}
                </Box>
              );
            }}
          />

          <TextField source="site_name" label={translate('resources.charging-sessions.list.site')} />

          <TextField source="charger_name" label={translate('resources.charging-sessions.list.charger')} />

          <DateField
            source="session_start_time"
            label={translate('resources.charging-sessions.list.startTime')}
            showTime
          />

          <FunctionField
            label={translate('resources.charging-sessions.list.type')}
            render={(record: any) => <InitiatedByChip initiatedBy={record.initiated_by} />}
          />

          <ShowButton label="" />
        </SessionDatagrid>
      )}
    </List>
  );
};

export default ChargingSessionsList;